import React from "react";

import { generateClient } from "aws-amplify/api";
import { Schema } from "../amplify/data/resource";
import {
  Badge,
  Card,
  Collection,
  Flex,
  Grid,
  Heading,
  Link,
} from "@aws-amplify/ui-react";
import DisciplineImage from "./components/DisciplineImage";
import { I18n } from "aws-amplify/utils";
import LoadingDialog from "./components/LoadingDialog";

const client = generateClient<Schema>();

type Routine = Schema["Routine"]["type"];
type DanceStyle = Schema["DanceStyle"]["type"];

export default function Home() {
  const [routines, setRoutines] = React.useState<Routine[]>([]);
  const [danceStyles, setDanceStyles] = React.useState<DanceStyle[]>([]);
  const [danceStylesMap, setDanceStylesMap] = React.useState<{
    [key: string]: DanceStyle;
  }>({});
  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  React.useEffect(() => {
    const loadRoutines = async () => {
      markLoading("routines", true);

      const { data: items, errors } = await client.models.Routine.list({
        filter: { status: { eq: true } },
        limit: 10,
      });
      if (errors) {
        console.error(errors);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setRoutines([...items]);

      markLoading("routines", false);
    };
    const loadDanceStyles = async () => {
      markLoading("danceStyles", true);

      const { data: items, errors } = await client.models.DanceStyle.list({
        filter: { status: { eq: true } },
        limit: 10,
      });
      if (errors) {
        console.error(errors);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setDanceStyles([...items]);

      markLoading("danceStyles", false);
    };
    loadRoutines();
    loadDanceStyles();
  }, []);

  React.useEffect(() => {
    const danceStylesMap = danceStyles.reduce(
      (acc, danceStyle) => {
        acc[danceStyle.id] = danceStyle;
        return acc;
      },
      {} as { [key: string]: DanceStyle }
    );
    setDanceStylesMap(danceStylesMap);
  }, [danceStyles]);

  return (
    <>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex direction="column" gap="1rem">
          <Flex marginTop="1rem">
            <Heading>{I18n.get("Routines")}</Heading>
          </Flex>

          <Grid>
            <Collection
              items={routines}
              type="list"
              direction="row"
              wrap="wrap"
            >
              {(routine) => (
                <Card
                  key={routine.id}
                  title={routine.name}
                  variation="outlined"
                  borderRadius="medium"
                  alignSelf="flex-start"
                  width={{ base: "100%", medium: "48.8%", large: "18.9%" }}
                >
                  <Grid marginBottom="1rem">
                    <Heading isTruncated columnStart="1" textAlign="center">
                      <Link href={`/routines/${routine.id}/view`}>
                        {routine.name}
                      </Link>
                    </Heading>
                  </Grid>
                  <Flex
                    direction="column"
                    wrap="wrap"
                    justifyContent="center"
                    marginBottom="1rem"
                  >
                    <Badge
                      backgroundColor={
                        routine.danceStyleRoutinesId &&
                        danceStylesMap[routine.danceStyleRoutinesId]
                          ?.discipline === "Latin"
                          ? "purple.40"
                          : "yellow.40"
                      }
                    >
                      <Link
                        href={`/dance-styles/${routine.danceStyleRoutinesId}/view`}
                        color={"black"}
                      >
                        {routine.danceStyleRoutinesId
                          ? danceStylesMap[routine.danceStyleRoutinesId]?.name
                          : ""}
                      </Link>
                    </Badge>
                  </Flex>
                </Card>
              )}
            </Collection>
          </Grid>
          <Flex>
            <Heading>{I18n.get("Dance Styles")}</Heading>
          </Flex>
          <Grid>
            <Collection
              items={danceStyles}
              type="list"
              direction="row"
              wrap="wrap"
            >
              {(danceStyle: DanceStyle) => (
                <Card
                  key={danceStyle.id}
                  title={danceStyle.name}
                  variation="outlined"
                  borderRadius="medium"
                  alignSelf="flex-start"
                  width={{ base: "100%", medium: "48.8%", large: "18.9%" }}
                >
                  <Flex direction="column" textAlign="center">
                    <Heading>
                      <Link href={`/dance-styles/${danceStyle.id}/view`}>
                        {danceStyle.name}
                      </Link>
                    </Heading>
                    <Link href={`/dance-styles/${danceStyle.id}/view`}>
                      <DisciplineImage
                        discipline={danceStyle.discipline ?? "Standard"}
                      />
                    </Link>
                  </Flex>
                </Card>
              )}
            </Collection>
          </Grid>
        </Flex>
      )}
    </>
  );
}
